import { TRadioSelectorInstance } from 'widgets/checkout/RadioSelector';
import { TDimensionalDropdownInstance } from 'core/forms/DimensionalDropdown';
import { TBasicInputInstance } from 'widgets/forms/BasicInput';
import { TRefElementInstance } from 'widgets/toolbox/RefElement';

type TOrderObject = { [key: string]: number | string | boolean | Array<number | string | boolean> };
type TCustomerObject = { [key: string]: number | string | boolean | Array<number | string | boolean> };

/**
 * @param AddressCheckoutForm Base widget for extending
 * @returns Core Address Checkout Form widget
 */
function AddressCheckoutFormClassCreator(AddressCheckoutForm: import('widgets/checkout/AddressCheckoutForm').TAddressCheckoutForm) {
    /**
     * @category widgets
     * @subcategory checkout
     * @class AddressCheckoutForm
     * @augments AddressForm
     * @property {string} data-address-selector - Saved addresses selector
     * @property {string} data-address-fields - List of address fields
     * @classdesc Checkout address form component, that has common functionality for billing ans shipping address.
     *
     * It has next features:
     * 1. Handles `edit` button click
     * 2. Handles `add new address` button click
     * 3. Handles return from new to saved addresses
     * 4. Handles saved address changing
     * 5. Filters similar addresses for address selector
     * 6. Validates form before submission
     *
     * * Widget has next relationship:
     * * {@link RadioSelector#hide}/{@link RadioSelector#show} is used to toggle saved addresses
     * * {@link RadioSelector#getDataSet} is used to get selected address properties
     * * {@link RadioSelector#getDataValue} is used to get selected address
     * * {@link RadioSelector#updateState} is used to rerender saved addresses list
     * * Also {@link TBasicInputInstance} methods are used to fill/clear/validate form fields
     * @example <caption>Checkout shipping form</caption>
     * <form
     *     class="b-form m-checkout_shipping"
     *     autocomplete="on"
     *     novalidate
     *     data-widget="shippingForm"
     *     data-event-submit.prevent="handleSubmit"
     *     data-widget-event-updateshippingform="updateSummaryEv"
     *     data-address-selector="shippingAddressSelector"
     *     data-address-fields="${pdict.shippingAddressFields.join(',')}"
     *     data-widget-event-submit="shippingSubmitted"
     *     data-select-shipping-method-url="${URLUtils.https('CheckoutShippingServices-SelectShippingMethod')}"
     *     data-update-shipping-list-url="${URLUtils.https('CheckoutShippingServices-UpdateShippingMethodsList')}"
     *     data-enabled-country-restriction="${pdict.customer.isCountryRestrictionEnabled}"
     *     action="${URLUtils.url('CheckoutShippingServices-SubmitShipping')}"
     *     data-form-definition-url="${URLUtils.url('Forms-FormDefinition', 'form', 'shipping')}"
     *     data-tau="checkout_shipping"
     *     <isprint value=${pdict.forms.shippingForm.attributes} encoding="off"/>
     *     data-address-autocomplete-enabled="${!!require('util/sitepref').getValue('addressSuggestionProvider').value}"
     * >
     *     ...
     *    <div
     *         class="b-form-message b-message m-error"
     *         role="alert"
     *         hidden="hidden"
     *         data-ref="errorMessageLabel"
     *     ></div>
     *     ...
     *     <isif condition="${pdict.addShipmentSelector}">
     *         <isinclude template="checkout/shipping/shipmentSelector" />
     *     </isif>
     *     ...
     *     <isinclude template="checkout/shipping/shippingAddress" />
     *     ...
     * </form>
     *
     */
    class CoreAddressCheckoutForm extends AddressCheckoutForm {
        prefs() {
            return {
                editAddressBtn: 'editAddressBtn',
                ...super.prefs()
            };
        }

        selectedAddressID = '';

        order?: TOrderObject;

        customer?: TCustomerObject;

        loadMoreClicked = false;

        /**
         * @description Prepare address options for displaying
         * @param order - order data
         * @param customer - customer data
         * @returns prepared options
         */
        getPreparedAddressOptions(order: unknown, customer: unknown) {
            const preparedAddressOptions = super.getPreparedAddressOptions(order, customer);

            preparedAddressOptions.customerAddresses = preparedAddressOptions.customerAddresses.map((address, index) => {
                return {
                    ...address,
                    isHidden: index > 1 && !this.loadMoreClicked
                };
            });

            return {
                ...preparedAddressOptions,
                showLoadMoreButton: preparedAddressOptions.customerAddresses.length > 2 && !this.loadMoreClicked
            };
        }

        /**
         * @description Initialize saved addresses selector
         * @returns Rendering promise or `undefined`
         */
        activateAddressSelector(): Promise<void | null> | undefined {
            return this.getById(this.prefs().addressSelector, (addressSelector: TRadioSelectorInstance) => {
                const preparedAddressOptions = this.getPreparedAddressOptions(this.order, this.customer);

                return addressSelector
                    .updateState({
                        addresses: preparedAddressOptions
                    })
                    .then(() => {
                        const dataset = addressSelector.getDataSet() as Record<string, string>;

                        if (dataset && dataset.value !== 'new') {
                            this.hideAddressForm();
                            this.fulfillAddressFromAddressSelector();
                        } else {
                            this.showAddressForm();
                        }

                        this.onAddressSelectorActivated();
                    });
            });
        }

        /**
         * @description Handle click on add new address button.
         * Toggle saved and new address blocks
         * @listens dom#click
         */
        addNewAddress() {
            super.addNewAddress();

            this.cleanTypeToSearch();
        }

        /**
         * Cleans up the `typeToSearch` property for each item in the `items` array.
         * If an item has a `typeToSearch` property, its `cleanTypeToSearch` method is called.
         */
        cleanTypeToSearch() {
            let index = 0;

            (this.items as Array<TDimensionalDropdownInstance>).forEach(item => {
                if (item.typeToSearch) {
                    item.cleanTypeToSearch();
                    index++;
                }

                if (index > 1) {
                    item.typeToSearch?.disable();
                }
            });
        }

        /**
         * @description Show saved addresses block
         * @param isBackFromEdit - Checks if addresses are shown after backing out of edit
         */
        showSavedAddresses(isBackFromEdit?: boolean | undefined) {
            this.getById(this.prefs().addressSelector, (addressSelector: TRadioSelectorInstance) => {
                addressSelector.show();

                if (isBackFromEdit) {
                    addressSelector.ref('editAddressBtn').get()?.focus();
                }
            });
        }

        /**
         * @description Handle click on back to saved addresses button
         * @listens dom#click
         */
        backToAddresses() {
            this.showSavedAddresses(true);
            this.changeAddress();
        }

        /**
         * @description Handle saved address change.
         * Pre-fill saved address to the address form
         * @listens BasicInput#change
         */
        changeAddress() {
            this.getById(this.prefs().addressSelector, (addressSelector: TRadioSelectorInstance) => {
                const addressID = addressSelector.getDataValue();

                this.setSelectedAddress(addressID);

                this.updateAddressSelector(() => {
                    this.fulfillAddressFromAddressSelector();
                    this.hideAddressForm();
                });
            });
        }

        /**
         * @description Handle edit button click on the saved address
         * *set selected address
         * *copy address to the from
         * *show address form
         * @param updateAddressBtn - Edit address button
         * @listens dom#click
         */
        updateAddress(updateAddressBtn: TRefElementInstance) {
            const addressID = updateAddressBtn && updateAddressBtn.data<string>('uuid');

            if (addressID) {
                this.getAddressFields().forEach(item => {
                    this.getById(item, (addressField: TBasicInputInstance) => {
                        addressField.clearError();
                    });
                });

                this.setSelectedAddress(addressID);
                this.updateAddressSelector(() => {
                    this.showAddressForm();
                    this.fulfillAddressFromAddressSelector();
                    // class AccessibilityFocusMixin, method focusFirst
                    this.callIfExists('focusFirstInput', 'addressForm');
                });
            }
        }
    }

    return CoreAddressCheckoutForm;
}

export type TAddressCheckoutForm = ReturnType<typeof AddressCheckoutFormClassCreator>;

export type TAddressCheckoutFormInstance = InstanceType<TAddressCheckoutForm>;

export default AddressCheckoutFormClassCreator;
